import React, { useEffect, forwardRef, useImperativeHandle, RefAttributes } from "react";
import InputGroup from 'react-bootstrap/InputGroup';
import { useODataContext } from "../contexts/ODataContext";
import {
    DropDownList,
    DropDownListChangeEvent,
    DropDownListFilterChangeEvent,
    DropDownListFocusEvent,
} from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { State, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { v4 as uuidv4 } from 'uuid';

interface DropDownListProps extends RefAttributes<DropDownListCustomHandle> {
    Label: string;
    PlaceHolder: string;
    TextField: string;
    KeyField: string;
    PageSize: number;   
    onChange?: (event: DropDownListChangeEvent) => void;
    resetValue?: () => void;
    id?: string;
    onFocus?: (event: DropDownListFocusEvent) => void;
    isInlineEdit?: boolean;
    AutoBind?: boolean;
    value?: any | undefined;
    disabled?: boolean;
}

export type DropDownListCustomHandle = {
    setValueFilter: (valueFilter: CompositeFilterDescriptor) => void;
    settingValue: (value: any) => void;
    refreshData: (url?: string) => void;
    openDropDown: () => void;
};

export const DropDownListCustom: React.FC<DropDownListProps> = forwardRef<DropDownListCustomHandle, DropDownListProps>((props, ref) => {

    const { data, total, loadData } = useODataContext();
    const delay = 500;
    const [initialFilters, setInitialFilters] = React.useState<any>(null);
    const [value, setValue] = React.useState<any>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const dropdownRef = React.useRef<any>(null);
    const timeout = React.useRef<any>(false);
    const [dataState, setDataState] = React.useState<State>({ take: 30, skip: 0, });
    const autoBindSet = props.AutoBind !== undefined ? props.AutoBind : true;
    useImperativeHandle(ref, () => ({
        setValueFilter, settingValue, refreshData, openDropDown
    }));

    const settingValue = (value: any) => {                
        setValue(value);
    }

    const openDropDown = () => {
        // Set the focus on current ddl and toggle the popup
        dropdownRef.current?.focus();
        setTimeout(() => dropdownRef.current?.togglePopup(true), 0);
    };

    const setValueFilter = (value: CompositeFilterDescriptor) => {
        setInitialFilters(value);
        dataState.filter = value;
        setDataState(dataState);
        loadData(dataState);
    }
    
    useEffect(() => {
        if(autoBindSet){
        loadData(dataState);
        if (props.value) {
            setValue(props.value);
        }
    }}, []);

    const onFilterChange = (event: DropDownListFilterChangeEvent) => {
        if (event.filter?.value === "") {
            dataState.filter = undefined;
            setDataState(dataState);
            loadData(dataState);
        }
        else {
            event.filter.value = event.filter.value.toLowerCase();
        }

        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            setLoading(false);

            if(initialFilters !== null && initialFilters !== undefined){
                if(event.filter?.value){
                    dataState.filter = {
                        logic: "and",
                        filters: [
                            initialFilters,
                            {
                                logic: "or",
                                filters: [event.filter]
                            }
                        ]
                    }
                }
                else {
                    dataState.filter = {
                        logic: "and",
                        filters: [initialFilters]
                    }
                }
            }
            else{
                dataState.filter = {
                    logic: "or",
                    filters: [event.filter]
                }
            }

            setDataState({ ...dataState});
            loadData(dataState);
        }, delay);
    }

    const refreshData = (url?: string)=>{
        loadData(dataState, url);
    }

    const handleChange = (event: DropDownListChangeEvent) => {
        setValue(event.target.value);
        if (props.onChange) {
            props.onChange(event);
        }
        event.nativeEvent.preventDefault();
    };

    const handleResetValue = (e: React.MouseEvent<HTMLButtonElement>) => {
        setValue([]);

        props.resetValue?.();
        e.preventDefault();
    };

    const handleFocus = (event: DropDownListFocusEvent) => {
        if (props.onFocus) {
            props.onFocus(event);            
        }
        event.nativeEvent.preventDefault();
    }

    return (
        <div>
            {props.Label?(<span className="subtitle">{props.Label}:</span>):null}
            <InputGroup className="mb-6">
                <DropDownList
                    ref={dropdownRef}
                    id={props.id ?? uuidv4()}
                    className="filter-dropdown"
                    data={data}
                    textField={props.TextField}
                    onChange={handleChange}
                    filterable={true}
                    dataItemKey={props.KeyField}
                    onFilterChange={onFilterChange}
                    onFocus={handleFocus}
                    value={value}
                    loading={loading}
                    virtual={
                        {
                            pageSize: props.PageSize,
                            skip: 0,
                            total: total,
                        }
                    }
                    disabled={props.disabled}
                    popupSettings={props.isInlineEdit?{
                        width: '300px'
                      }:{}}
                />
                {!props.isInlineEdit?
                (<Button themeColor="secondary" style={{ border: "1px solid", borderColor: "#6c757d" }} onClick={handleResetValue}>
                    <SvgIcon icon={svgIcons.xIcon} size="medium" />
                </Button>)
                :null
                }
                
            </InputGroup>
        </div>
    );
});
